import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../../../services/api';
import { FiSave, FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import PermissionButton from '../../../components/PermissionButton';
import { getAuth } from '../../../utils/auth';

const UserEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userData: currentUser } = getAuth();
  
  // Check if the user is editing themselves
  const isEditingSelf = currentUser?.id === id;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    password: ''
  });

  // Fetch user details
  const { data: user, isLoading, isError, error } = useQuery(
    ['admin-user', id],
    () => adminAPI.getUser(id),
    {
      enabled: !!id
    }
  );

  // Fetch available roles
  const { data: rolesData } = useQuery(
    'admin-roles',
    () => adminAPI.getRoles(),
    {
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  );

  // Filter out customer roles for internal user management
  const roles = rolesData?.data || [];
  const internalRoles = roles.filter(role => 
    !['customer_admin', 'regular_user'].includes(role.name)
  );

  // Helper function to format role display name
  const formatRoleName = (role) => {
    return role.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Update user mutation
  const updateUserMutation = useMutation(
    (data) => adminAPI.updateUser(id, {
      ...data,
      userType: 'internal' // Always internal for admin-managed users
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-user', id]);
        queryClient.invalidateQueries('admin-users');
        navigate(`/admin/users/${id}`);
      }
    }
  );

  // Toggle user status mutation  
  const toggleStatusMutation = useMutation(
    ({ activate }) => 
      activate ? adminAPI.activateUser(id) : adminAPI.deactivateUser(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-user', id]);
        queryClient.invalidateQueries('admin-users');
      }
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    () => adminAPI.deleteUser(id),
    {
      onSuccess: () => {
        navigate('/admin/users');
      }
    }
  );

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.roles?.[0]?.name || '',
        password: ''
      });
    }
  }, [user]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <ErrorAlert message={error?.message || 'Error loading user details'} />;
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const updateData = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      // Only include password if it was changed
      ...(formData.password && { password: formData.password })
    };
    await updateUserMutation.mutateAsync(updateData);
  };

  const handleToggleStatus = async () => {
    const action = user?.is_active ? 'deactivate' : 'activate';
    const confirmed = window.confirm(`Are you sure you want to ${action} this user?`);
    
    if (confirmed) {
      await toggleStatusMutation.mutateAsync({ activate: !user?.is_active });
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) {
      await deleteUserMutation.mutateAsync();
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Edit Internal User</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Update user information and account details.</p>
          </div>
          {user?.is_active ? (
            <PermissionButton
              permissions="manage_users"
              onClick={handleToggleStatus}
              disabled={toggleStatusMutation.isLoading || isEditingSelf}
              className="border-red-300 text-red-700 bg-white hover:bg-red-50 focus:ring-red-500"
              title={isEditingSelf ? "You cannot deactivate your own account" : ""}
            >
              <FiAlertTriangle className="mr-2 h-4 w-4" />
              {toggleStatusMutation.isLoading ? 'Deactivating...' : 'Deactivate User'}
            </PermissionButton>
          ) : (
            <PermissionButton
              permissions="delete_users"
              onClick={handleDelete}
              disabled={deleteUserMutation.isLoading || isEditingSelf}
              className="border-red-300 text-red-700 bg-white hover:bg-red-50 focus:ring-red-500"
              title={isEditingSelf ? "You cannot delete your own account" : ""}
            >
              <FiTrash2 className="mr-2 h-4 w-4" />
              {deleteUserMutation.isLoading ? 'Deleting...' : 'Delete User'}
            </PermissionButton>
          )}
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Full name
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    name="name"
                    id="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role
                </label>
                <div className="mt-1">
                  <select
                    name="role"
                    id="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    required
                  >
                    <option value="">Select a role...</option>
                    {internalRoles.map(role => (
                      <option key={role.id} value={role.name}>
                        {formatRoleName(role.name)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  New Password (leave blank to keep current)
                </label>
                <div className="mt-1">
                  <input
                    type="password"
                    name="password"
                    id="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>

            {/* User Status Display */}
            <div className="bg-gray-50 px-4 py-3 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Current Status</h4>
                  <p className="text-sm text-gray-500">
                    This user is currently {user?.is_active ? 'active' : 'inactive'}
                  </p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user?.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {user?.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate(`/admin/users/${id}`)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateUserMutation.isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                <FiSave className="mr-2 -ml-1 h-4 w-4" />
                {updateUserMutation.isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserEdit; 